import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindAllAgecStatsQueryResponse,
  FindAllAgecStatsQueryParams,
} from "../types/FindAllAgecStats"

type FindAllAgecStatsClient = typeof client<
  FindAllAgecStatsQueryResponse,
  Error,
  never
>
type FindAllAgecStats = {
  data: FindAllAgecStatsQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindAllAgecStatsQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindAllAgecStatsClient>>
  client: {
    parameters: Partial<Parameters<FindAllAgecStatsClient>[0]>
    return: Awaited<ReturnType<FindAllAgecStatsClient>>
  }
}
export const findAllAgecStatsQueryKey = (
  params: FindAllAgecStats["queryParams"]
) =>
  [
    { url: "/v1/product/find-all-agec-stats" },
    ...(params ? [params] : []),
  ] as const
export type FindAllAgecStatsQueryKey = ReturnType<
  typeof findAllAgecStatsQueryKey
>
export function findAllAgecStatsQueryOptions<
  TData = FindAllAgecStats["response"],
  TQueryData = FindAllAgecStats["response"],
>(
  params: FindAllAgecStats["queryParams"],
  options: FindAllAgecStats["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindAllAgecStats["response"],
    FindAllAgecStats["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findAllAgecStatsQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindAllAgecStats["data"],
        FindAllAgecStats["error"]
      >({
        method: "get",
        url: `/v1/product/find-all-agec-stats`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find all agec stats
 * @link /v1/product/find-all-agec-stats
 */
export function useFindAllAgecStats<
  TData = FindAllAgecStats["response"],
  TQueryData = FindAllAgecStats["response"],
  TQueryKey extends QueryKey = FindAllAgecStatsQueryKey,
>(
  params: FindAllAgecStats["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindAllAgecStats["response"],
        FindAllAgecStats["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindAllAgecStats["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindAllAgecStats["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findAllAgecStatsQueryKey(params)
  const query = useQuery<
    FindAllAgecStats["data"],
    FindAllAgecStats["error"],
    TData,
    any
  >({
    ...findAllAgecStatsQueryOptions<TData, TQueryData>(params, clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindAllAgecStats["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
